import { TypedDocumentNode, gql } from '@apollo/client';
import { useContext, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { CSSProperties } from 'styled-components';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { AnimatedNumber } from '@sorare/core/src/atoms/animations/AnimatedNumber';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { NewScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { Portal } from '@sorare/core/src/atoms/layout/Portal';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { Snackbar } from '@sorare/core/src/atoms/snackbar/Snackbar';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { glossary } from '@sorare/core/src/lib/glossary';
import {
  laptopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { useDropAndAddCards } from '@sorare/marketplace/src/components/commonCard/CommonPaymentBox/useDropAndAddCards';
import commonEssence from '@sorare/marketplace/src/components/commonCard/SellCommonCard/assets/common-essence.png';

import { MyCardsLayoutContext } from 'layouts/my-cards/MyCardsLayout/MyCardsLayoutContext';

import {
  CardsDropSummaryQuery,
  CardsDropSummaryQueryVariables,
} from './__generated__/index.graphql';

const Wrapper = styled(Vertical)`
  position: sticky;
  top: var(--current-stack-height);
  margin-left: calc(-1 * var(--unit));
  margin-right: calc(-3 * var(--unit));
  padding: var(--unit) var(--double-unit);
  background: var(--c-nd-100-opaque);
  align-items: center;
  @media ${tabletAndAbove} {
    flex-direction: row;
    justify-content: space-between;
  }
  z-index: 1;
`;

const MobileOnly = styled.div`
  position: fixed;
  isolation: isolate;
  bottom: var(--unit);
  left: var(--double-unit);
  right: var(--double-unit);
  transform: translateY(
    calc(var(--bottom-sheet-height) * -1 - var(--bottom-bar-height-mobile))
  );
  transition: transform 0.1s ease-out;
  @media ${laptopAndAbove} {
    display: none;
  }
`;
const MobileButtons = styled(SBHorizontal)`
  z-index: 1000;
  width: 100%;
  & > * {
    flex: 1;
  }
`;

const LaptopOnly = styled.div`
  display: none;
  @media ${laptopAndAbove} {
    display: block;
  }
`;

type Props = {
  onCancel?: () => void;
  selectedCardsSlugs: Nullable<string[]>;
  canSellCommon: Nullable<boolean>;
};

const CARDS_DROP_SUMMARY_QUERY = gql`
  query CardsDropSummaryQuery($slugs: [String!]!) {
    anyCards(slugs: $slugs) {
      slug
      signedDropCardPrice {
        price
        signedPrice
      }
    }
  }
` as TypedDocumentNode<CardsDropSummaryQuery, CardsDropSummaryQueryVariables>;

export const CardsDropSummary = ({
  onCancel,
  selectedCardsSlugs,
  canSellCommon,
}: Props) => {
  const { sport } = useSportContext();

  const { data } = useQuery(CARDS_DROP_SUMMARY_QUERY, {
    variables: {
      slugs: selectedCardsSlugs || [],
    },
    skip: !selectedCardsSlugs?.length,
  });
  const selectedCards =
    data?.anyCards?.filter(card => selectedCardsSlugs?.includes(card.slug)) ||
    [];
  const totalPoints = selectedCards.reduce(
    (acc, card) => acc + (card.signedDropCardPrice?.price || 0),
    0
  );

  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const { dropAndAddCards, loading: mutationLoading } = useDropAndAddCards();
  const { bottomSheetHeight } = useContext(MyCardsLayoutContext);

  const buttons = (
    <>
      <Button color="tertiary" size="medium" onClick={onCancel}>
        <FormattedMessage {...glossary.cancel} />
      </Button>
      <LoadingButton
        loading={mutationLoading}
        size="medium"
        onClick={async () => {
          const result = await dropAndAddCards(
            selectedCards
              .filter(card => !!card.signedDropCardPrice)
              .map(card => ({
                cardSlug: card.slug,
                signedPrice: card.signedDropCardPrice!.signedPrice,
              })),
            []
          );
          if (result.data?.dropAndAddCards?.droppedCards?.length) {
            setSnackbarMessage(`You gained ${totalPoints} Common Essence`);
            onCancel?.();
          }
        }}
        disabled={!selectedCardsSlugs?.length}
      >
        <FormattedMessage {...glossary.confirm} />
      </LoadingButton>
    </>
  );
  return (
    <>
      {canSellCommon && sport === Sport.FOOTBALL && (
        <Wrapper>
          <Horizontal>
            <NewScarcityIcon rarity={Rarity.common} />
            <LabelL>
              <FormattedMessage
                id="CardsDropSummary.cardsDropped"
                defaultMessage="{count, plural, one {# Card dropped} other {# Cards dropped}}"
                values={{
                  count: selectedCardsSlugs?.length || 0,
                }}
              />
            </LabelL>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="4"
              height="4"
              viewBox="0 0 4 4"
              fill="none"
            >
              <circle cx="2" cy="2" r="2" fill="white" fillOpacity="0.17" />
            </svg>
            <img src={commonEssence} alt="" height={32} />
            <LabelL>
              + <AnimatedNumber value={totalPoints} />
            </LabelL>
          </Horizontal>
          <LaptopOnly>
            <Horizontal>{buttons}</Horizontal>
          </LaptopOnly>
          <Portal id="full-screen-animation">
            <MobileOnly
              style={
                {
                  '--bottom-sheet-height': `${bottomSheetHeight}px`,
                } as CSSProperties
              }
            >
              <SBHorizontal>
                <MobileButtons>{buttons}</MobileButtons>
              </SBHorizontal>
            </MobileOnly>
          </Portal>
          <Snackbar
            open={!!snackbarMessage}
            onClose={() => setSnackbarMessage(null)}
            message={snackbarMessage}
            autoHideDuration={10000}
          />
        </Wrapper>
      )}
    </>
  );
};
